// Destinations map (port of DestinationsMapPage.tsx + InteractiveRegionMap.tsx).
import L from 'leaflet';
import { getMapDestinations } from '../lib/api.js';
import {
  MAP_TRIP_TYPES, destinationMatchesTripTypes, filterDestinationsByTripTypes,
  tripTypeToSearchParam, countryFlagEmoji,
} from '../lib/mapTripTypes.js';
import { trackFlightSearchSubmitted } from '../lib/flightSearchAnalytics.js';

const ACCENT = '#00C2B0';
const NAVY = '#0A1E38';
const REGION_LABELS = { all: 'All regions', europe: 'Europe', iceland: 'Iceland', 'north-africa': 'North Africa', turkey: 'Turkey' };

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function destMarkerIcon(selected) {
  const sz = selected ? 18 : 14;
  return L.divIcon({ className: '', html: `<div style="width:${sz}px;height:${sz}px;border-radius:50%;background:${selected ? ACCENT : NAVY};border:3px solid #fff;box-shadow:0 2px 10px rgba(0,0,0,0.28);transition:transform 0.2s;"></div>`, iconAnchor: [selected ? 9 : 7, selected ? 9 : 7] });
}
function placeMarkerIcon() {
  return L.divIcon({ className: '', html: `<div style="width:10px;height:10px;border-radius:50%;background:#fff;border:2.5px solid ${ACCENT};box-shadow:0 1px 6px rgba(0,0,0,0.2);"></div>`, iconAnchor: [5, 5] });
}
function airportMarkerIcon(code) {
  return L.divIcon({ className: '', html: `<div style="background:${NAVY};color:#fff;padding:4px 9px;border-radius:7px;font-family:'DM Sans',sans-serif;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 2px 10px rgba(0,0,0,0.25);">✈ ${esc(code)}</div>`, iconAnchor: [24, 10] });
}
function scatterPlaces(city, count) {
  const points = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    const radius = 0.012 + (i % 3) * 0.004;
    points.push([city.lat + Math.sin(angle) * radius, city.lng + Math.cos(angle) * radius]);
  }
  return points;
}
function applyMapView(map, bounds, opts = {}) {
  if (!map.getContainer().isConnected) return;
  map.stop();
  map.invalidateSize();
  const fit = { padding: opts.padding ?? [40, 40], ...(opts.maxZoom != null ? { maxZoom: opts.maxZoom } : {}) };
  if (opts.animate) map.flyToBounds(bounds, { ...fit, duration: 0.8 });
  else map.fitBounds(bounds, fit);
}

function start(data) {
  const root = document.querySelector('.destinations-map');
  const state = { origin: 'LHR', dest: null, region: 'all', tripTypes: new Set(), view: 'overview' };
  let prevRegion = 'all';
  let prevDest = null;

  // Origins select
  const sel = root.querySelector('[data-origin-select]');
  sel.innerHTML = data.origins.map((o) => `<option value="${esc(o.code)}">${esc(o.name)} (${esc(o.code)})</option>`).join('');
  if (data.origins.some((o) => o.code === 'LHR')) sel.value = 'LHR';
  else if (data.origins[0]) state.origin = sel.value = data.origins[0].code;

  // Map init
  root.querySelector('[data-loading]').hidden = true;
  const map = L.map(root.querySelector('[data-map]'), { center: [48, 10], zoom: 4, zoomControl: true, scrollWheelZoom: false, dragging: false, doubleClickZoom: false });
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { attribution: '© OpenStreetMap contributors © CARTO', maxZoom: 19 }).addTo(map);
  const layers = { routes: L.layerGroup().addTo(map), places: L.layerGroup().addTo(map), local: L.layerGroup().addTo(map) };
  const destMarkers = new Map();
  for (const dest of data.destinations) {
    const marker = L.marker([dest.city.lat, dest.city.lng], { icon: destMarkerIcon(false) }).addTo(map);
    marker.on('click', () => { selectDest(dest); });
    marker.bindTooltip(dest.name, { direction: 'top', offset: [0, -8], className: 'dest-map-tooltip' });
    destMarkers.set(dest.slug, marker);
  }
  applyMapView(map, data.regionBounds.all, { animate: false });
  requestAnimationFrame(() => {
    map.invalidateSize();
    applyMapView(map, data.regionBounds.all, { animate: false });
    map.dragging.enable(); map.scrollWheelZoom.enable(); map.doubleClickZoom.enable();
    apply();
  });

  const isVisible = (dest) => (state.region === 'all' || dest.region === state.region) && destinationMatchesTripTypes(dest, state.tripTypes);

  function drawRoutes() {
    layers.routes.clearLayers();
    const origin = data.origins.find((o) => o.code === state.origin);
    if (!origin) return;
    for (const dest of data.destinations.filter(isVisible)) {
      L.polyline([[origin.lat, origin.lng], [dest.airport.lat, dest.airport.lng]], { color: ACCENT, weight: 2, dashArray: '7 6', opacity: state.dest?.slug === dest.slug ? 0.9 : 0.35 }).addTo(layers.routes);
    }
    L.marker([origin.lat, origin.lng], { icon: airportMarkerIcon(origin.code) }).addTo(layers.routes);
  }

  function showDetail(dest) {
    layers.places.clearLayers(); layers.local.clearLayers();
    L.polyline([[dest.airport.lat, dest.airport.lng], [dest.city.lat, dest.city.lng]], { color: ACCENT, weight: 3, dashArray: '9 7', opacity: 0.85 }).addTo(layers.local);
    L.marker([dest.city.lat, dest.city.lng], { icon: destMarkerIcon(true) }).addTo(layers.local);
    const coords = scatterPlaces(dest.city, dest.places.length);
    dest.places.forEach((place, i) => {
      const [lat, lng] = coords[i] ?? [dest.city.lat, dest.city.lng];
      L.marker([lat, lng], { icon: placeMarkerIcon() }).addTo(layers.places)
        .bindPopup(`<strong style="font-family:'DM Sans',sans-serif;font-size:13px;">${esc(place.name)}</strong><br><span style="color:#6B7280;font-size:11px;">${esc(place.type)}</span>`);
    });
    applyMapView(map, [[dest.airport.lat, dest.airport.lng], [dest.city.lat, dest.city.lng], ...coords], { animate: true, padding: [80, 80], maxZoom: 12 });
  }

  // Mirror of React effects B + C.
  function apply() {
    drawRoutes();
    for (const [slug, marker] of destMarkers) {
      const dest = data.destinations.find((d) => d.slug === slug);
      if (!dest) continue;
      if (isVisible(dest)) marker.addTo(map); else marker.remove();
      marker.setIcon(destMarkerIcon(state.dest?.slug === slug));
    }
    if (state.dest) {
      showDetail(state.dest);
      for (const [slug, marker] of destMarkers) marker.setOpacity(slug === state.dest.slug ? 1 : 0.25);
      prevDest = state.dest; prevRegion = state.region;
    } else {
      layers.places.clearLayers(); layers.local.clearLayers();
      for (const marker of destMarkers.values()) marker.setOpacity(1);
      if (prevRegion !== state.region || prevDest !== null) applyMapView(map, data.regionBounds[state.region], { animate: true });
      prevDest = null; prevRegion = state.region;
    }
    renderSidebar();
  }

  function renderSidebar() {
    const aside = root.querySelector('[data-sidebar]');
    if (state.view === 'destination' && state.dest) {
      const d = state.dest;
      const guideLink = d.hasGuide ? `<a href="/destinations/${esc(d.slug)}" class="destinations-map__guide-link">Full travel guide →</a>` : '';
      aside.innerHTML = `<div class="destinations-map__detail">
        <button type="button" class="destinations-map__detail-back" data-back>← Back to map</button>
        <div class="destinations-map__detail-header">
          <span class="destinations-map__detail-flag">${countryFlagEmoji(d.flag)}</span>
          <div><h2 class="destinations-map__detail-name">${esc(d.name)}</h2><p class="destinations-map__detail-country">${esc(d.country)}</p></div>
        </div>
        ${d.tagline ? `<p class="destinations-map__detail-tagline">${esc(d.tagline)}</p>` : ''}
        <div class="destinations-map__detail-meta"><span>${esc(d.airport.code)}</span><span>${esc(d.flightTime)} from UK</span></div>
        <h3 class="destinations-map__places-title">Places to visit</h3>
        <ul class="destinations-map__places-list">${d.places.map((p) => `<li class="destinations-map__place-item"><span class="destinations-map__place-name">${esc(p.name)}</span><span class="destinations-map__place-type">${esc(p.type)}</span></li>`).join('')}</ul>
        <div class="destinations-map__detail-actions">${guideLink}<button type="button" class="destinations-map__search-link" data-search-flights>Search flights</button></div>
      </div>`;
      aside.querySelector('[data-back]').addEventListener('click', back);
      const searchBtn = aside.querySelector('[data-search-flights]');
      if (searchBtn) {
        searchBtn.addEventListener('click', () => {
          const tripType = tripTypeToSearchParam(state.tripTypes);
          trackFlightSearchSubmitted({
            fly_from: state.origin,
            fly_to: d.airport.code,
            trip_type: tripType,
            source: 'destinations_map',
            submit_pathname: window.location.pathname,
            adults: 1,
          });
          const params = new URLSearchParams({
            fly_from: state.origin,
            fly_to: d.airport.code,
            tripType,
            adults: '1',
          });
          window.location.href = `/flight-results?${params.toString()}`;
        });
      }
    } else {
      const visible = filterDestinationsByTripTypes(state.region === 'all' ? data.destinations : data.destinations.filter((d) => d.region === state.region), state.tripTypes);
      const title = state.region === 'all' ? 'All destinations' : REGION_LABELS[state.region];
      const hint = state.tripTypes.size > 0 ? `Showing ${visible.length} destinations for selected trip types.` : (state.origin ? `Routes shown from ${esc(state.origin)} to each airport.` : 'Select an airport to see route lines.');
      aside.innerHTML = `<div class="destinations-map__list">
        <h2 class="destinations-map__list-title">${esc(title)}<span class="destinations-map__list-count">${visible.length}</span></h2>
        <p class="destinations-map__list-hint">${hint}</p>
        <ul class="destinations-map__dest-list">${visible.map((dest) => `<li><button type="button" class="destinations-map__dest-btn${state.dest?.slug === dest.slug ? ' destinations-map__dest-btn--active' : ''}" data-dest="${esc(dest.slug)}">
          <span class="destinations-map__dest-flag">${countryFlagEmoji(dest.flag)}</span>
          <span class="destinations-map__dest-info"><span class="destinations-map__dest-name">${esc(dest.name)}</span><span class="destinations-map__dest-sub">${esc(dest.country)} · ${esc(dest.airport.code)} · ${esc(dest.flightTime)}</span></span>
          ${dest.hasGuide ? '<span class="destinations-map__dest-badge">Guide</span>' : ''}
        </button></li>`).join('')}</ul>
      </div>`;
      aside.querySelectorAll('[data-dest]').forEach((btn) => btn.addEventListener('click', () => {
        const dest = data.destinations.find((d) => d.slug === btn.getAttribute('data-dest'));
        if (dest) selectDest(dest);
      }));
    }
  }

  function selectDest(dest) { state.dest = dest; state.view = 'destination'; apply(); }
  function back() { state.dest = null; state.view = 'overview'; apply(); }

  // Controls
  sel.addEventListener('change', () => { state.origin = sel.value; apply(); });
  root.querySelectorAll('[data-region]').forEach((btn) => btn.addEventListener('click', () => {
    state.region = btn.getAttribute('data-region'); state.dest = null; state.view = 'overview';
    root.querySelectorAll('[data-region]').forEach((b) => b.classList.toggle('destinations-map__region-btn--active', b === btn));
    apply();
  }));
  root.querySelectorAll('[data-trip]').forEach((btn) => btn.addEventListener('click', () => {
    const id = btn.getAttribute('data-trip');
    if (state.tripTypes.has(id)) state.tripTypes.delete(id); else state.tripTypes.add(id);
    btn.classList.toggle('destinations-map__trip-btn--active', state.tripTypes.has(id));
    btn.setAttribute('aria-pressed', String(state.tripTypes.has(id)));
    state.dest = null; state.view = 'overview';
    apply();
  }));
}

function init() {
  const root = document.querySelector('.destinations-map');
  if (!root) return;
  getMapDestinations().then(start).catch((e) => {
    const err = root.querySelector('[data-error]');
    if (err) { err.textContent = e.message; err.hidden = false; }
    const loading = root.querySelector('[data-loading]');
    if (loading) loading.hidden = true;
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
